import type { ZodType } from "zod";

const API_BASE = "/api/v1";

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;

  constructor(
    code: string,
    message: string,
    status: number,
    details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

type ApiEnvelope<T> = {
  success: boolean;
  data?: T;
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
  };
  meta?: Record<string, unknown>;
};

export function csrfToken(): string {
  for (const part of document.cookie.split(";")) {
    const [name, ...value] = part.trim().split("=");
    if (name === "csrftoken") {
      return decodeURIComponent(value.join("="));
    }
  }
  return "";
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
  schema?: ZodType<T>,
): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const method = (options.method ?? "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS", "TRACE"].includes(method)) {
    const token = csrfToken();
    if (token && !headers.has("X-CSRFToken")) {
      headers.set("X-CSRFToken", token);
    }
  }

  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "same-origin",
    ...options,
    headers,
  });

  let body: ApiEnvelope<T>;
  try {
    body = (await response.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(
      `HTTP_${response.status}`,
      response.statusText || "The server returned an invalid response.",
      response.status,
    );
  }

  if (!response.ok || body.success !== true || body.data === undefined) {
    throw new ApiError(
      body.error?.code ?? `HTTP_${response.status}`,
      body.error?.message ?? response.statusText ?? "Request failed.",
      response.status,
      body.error?.details,
    );
  }

  return schema ? schema.parse(body.data) : body.data;
}

export function apiGet<T>(path: string, schema?: ZodType<T>): Promise<T> {
  return apiRequest(path, undefined, schema);
}

export function apiPost<T>(
  path: string,
  payload: unknown,
  schema?: ZodType<T>,
  headers?: HeadersInit,
): Promise<T> {
  return apiRequest(
    path,
    {
      method: "POST",
      body: JSON.stringify(payload),
      headers,
    },
    schema,
  );
}
